import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import classnames from 'classnames';
import emptyFunction from 'common/util/emptyFunction';

import {
  JOB_TYPE_SUNLOCK_INSTALL,
  JOB_TYPE_YARD_SIGN_SETUP,
  JOB_TYPE_CODEBOX_INSTALL,
  JOB_TYPE_MEDIA,
  JOB_TYPE_CONDITION_REPORT,
  JOB_TYPE_REPLACE_SUNLOCK_BATTERY,
  JOB_TYPE_CHECK_DOOR_LOCK,
  JOB_TYPE_COMBO_LOCK_INSTALL,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_FAILED,
  srJobStatusToDriverStatusMapping,
  getJobTypeToActionMapping,
  JOB_TYPE_MEDIA_CURBSHOT,
  JOB_TYPE_MEDIA_3D_TOUR,
  JOB_TYPE_CONFIRM_LISTING_INFORMATION,
  JOB_TYPE_SUNLOCK_TAKEDOWN,
  JOB_TYPE_CODEBOX_TAKEDOWN,
  JOB_TYPE_COMBO_LOCK_TAKEDOWN,
  JOB_TYPE_YARD_SIGN_TAKEDOWN,
  JOB_TYPE_SUPRA_LOCK_INSTALL,
  JOB_TYPE_SUPRA_LOCK_TAKEDOWN,
  JOB_TYPE_PROPERTY_CHECK,
} from 'common/jobConstants';

import { PARTNER_ID_HOMERIVER } from 'common/partnerConstants';
import { additionalDetailsFields } from 'common/listingFormFields';

import {
  scrollToFormField,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import ButtonDeprecated, {
  BUTTON_THEME_RED_ADOBE,
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import { SR_CHECKBOX_THEME_INLINE_WRAP } from 'client/components/Checkbox';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSunSetupDriverJobSelfTourTest from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSelfTourTest';
import SrSunSetupDriverJobSunlockBarcode from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSunlockBarcode';
import SrSunSetupDriverJobCodeboxBarcode from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxBarcode';
import SrSunSetupDriverJobYardSignSetup from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobYardSignSetup';
import SrSunSetupDriverJobCompletionPhotos from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCompletionPhotos';
import SrSunSetupDriverJobMediaPhotos from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobMediaPhotos';
import SrSunSetupDriverJobConditionReportLink from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobConditionReportLink';
import SrSunSetupDriverJobSunlockCodes from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSunlockCodes';
import SrSunSetupDriverJobCodeboxCodes from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxCodes';
import SrSunSetupDriverJobCodeboxLocationPhotos from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxLocationPhotos';
import SrSunSetupDriverJobCodeboxSpecialInstructions from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobCodeboxSpecialInstructions';
import SrSunSetupDriverJobConfirmListingInformation from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobConfirmListingInformation';
import SrSunSetupDriverJobComboLockboxCodes from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobComboLockboxCodes';
import SrSunSetupDriverJobYardSignTakedown from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobYardSignTakedown';
import SrSunSetupDriverJobComboLockboxBarcode from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobComboLockboxBarcode';
import SrSunSetupDriverJobSupraBarcode from 'client/components/SrSunSetupDriverJob/SrSunSetupDriverJobSupraBarcode';
import getJobChecklist from 'client/components/SrSunSetupDriverJob/getJobChecklist';

import './SrSunSetupDriverJobPage.less';

function getFormFields({
  job,
  listing,
  sunlock,
  codeboxLock,
  showJobCompletionPhotosFormField,
  formValues = {},
  onCompletionPhotoUrls,
  onCodeboxLocationPhotoUrls,
  onCodeboxInstructions,
  onDriverNotesChange,
  onChecklistChange,
  onDetailsChange,
  isAdmin,
}) {
  let formFields = [
    {
      id: 'status',
      type: 'hidden',
    },
  ];

  const checklist = getJobChecklist({ job, listing });
  if (checklist) {
    formFields.push({
      id: 'checklist',
      title: 'Instructions',
      type: 'multiple_choice',
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['checklistCompleted'] : [],
      properties: {
        autoFitWidth: '100%',
        multipleSelection: true,
        useArrayValues: true,
        choices: checklist.map((point) => ({
          label: point.label,
          value: point.value,
        })),
        onChange: onChecklistChange,
        fieldType: SR_CHECKBOX_THEME_INLINE_WRAP,
        noMargin: true,
      },
    });
  }

  if (showJobCompletionPhotosFormField) {
    formFields.push({
      id: 'completionPhotoUrls',
      title: '',
      type: 'file_multi',
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['nonEmptyArray'] : [],
      properties: {
        filePath: '/job-uploads/completion-photos/{uuid}',
        fileAccept: 'image/*, .heic',
        onChange: onCompletionPhotoUrls,
      },
    });
  }

  formFields.push({
    id: 'driverNotes',
    title: 'Driver Notes',
    type: 'long_text',
    validations: formValues.status === JOB_STATUS_FAILED ? ['required'] : [],
    properties: {
      placeholder: 'Write notes here...',
      onBlur: onDriverNotesChange,
      className: 'noMargin',
    },
  });

  if (
    job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL ||
    job.jobTypeId === JOB_TYPE_REPLACE_SUNLOCK_BATTERY
  ) {
    formFields.push({
      id: 'sunlockId',
      type: 'hidden',
      validations:
        formValues.status === JOB_STATUS_COMPLETED ? ['required::Please register a sunlock.'] : [],
    });
  }

  if (job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL) {
    formFields.push({
      id: 'codeboxLockId',
      type: 'hidden',
      validations:
        formValues.status === JOB_STATUS_COMPLETED ? ['required::Please register a codebox.'] : [],
    });

    formFields.push({
      id: 'codeboxLocationPhotos',
      title: '',
      type: 'file_multi',
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['nonEmptyArray'] : [],
      properties: {
        filePath: '/job-uploads/codebox-location-photos/{uuid}',
        fileAccept: 'image/*, .heic',
        onChange: onCodeboxLocationPhotoUrls,
      },
    });

    formFields.push({
      id: 'codeboxInstructions',
      title: '',
      type: 'long_text',
      properties: {
        placeholder: 'Write here...',
        onBlur: onCodeboxInstructions,
      },
    });
  }

  if (
    job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL ||
    job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL ||
    job.jobTypeId === JOB_TYPE_REPLACE_SUNLOCK_BATTERY ||
    job.jobTypeId === JOB_TYPE_CHECK_DOOR_LOCK ||
    job.jobTypeId === JOB_TYPE_PROPERTY_CHECK
  ) {
    formFields.push({
      id: 'successfulTest',
      type: 'hidden',
      validations:
        formValues.status === JOB_STATUS_COMPLETED && (sunlock || codeboxLock)
          ? ['lockSuccessfulTest']
          : [],
    });
  }

  if (job.jobTypeId === JOB_TYPE_YARD_SIGN_SETUP && listing.partnerId !== PARTNER_ID_HOMERIVER) {
    formFields.push({
      id: 'yardSignUrl',
      type: 'hidden',
      validations:
        formValues.status === JOB_STATUS_COMPLETED
          ? ['required::Please register the yard sign.']
          : [],
    });
  }

  if (job.jobTypeId === JOB_TYPE_MEDIA || job.jobTypeId === JOB_TYPE_MEDIA_CURBSHOT) {
    formFields.push({
      id: 'listingPhotoUrls',
      type: 'hidden',
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['nonEmptyArray'] : [],
    });
  }

  if (job.jobTypeId === JOB_TYPE_MEDIA_3D_TOUR) {
    formFields.push({
      id: 'details.3dTourUrl',
      type: 'short_text',
      title: '3D Tour Link',
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['required', 'url'] : [],
      properties: {
        placeholder: 'Paste here...',
        className: 'noMargin',
        onChange: onDetailsChange
          ? (event) => {
              onDetailsChange('3dTourUrl')(event.target.value);
            }
          : null,
      },
    });
  }

  if (job.jobTypeId === JOB_TYPE_CONFIRM_LISTING_INFORMATION) {
    formFields.push({
      id: 'details.verifyBedrooms',
      type: 'number',
      properties: {
        placeholder: 'Write number of bedrooms here....',
      },
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['required'] : [],
    });

    formFields.push({
      id: 'details.verifyBathrooms',
      type: 'number',
      properties: {
        placeholder: 'Write number of bathrooms here....',
      },
      validations: formValues.status === JOB_STATUS_COMPLETED ? ['required'] : [],
    });

    formFields.push(
      ...additionalDetailsFields.map((formField) => ({
        ...formField,
        id: `details.${formField.id}`,
        validations: formValues.status === JOB_STATUS_COMPLETED ? ['required'] : [],
        properties: {
          onChange: onDetailsChange ? onDetailsChange(formField.id) : null,
        },
      })),
    );
  }

  if (job.jobTypeId === JOB_TYPE_COMBO_LOCK_INSTALL) {
    formFields.push({
      id: 'details.lockboxCode',
      type: 'short_text',
      properties: {
        placeholder: 'Write code here....',
      },
      validations: ['required'],
    });
  }

  if (isAdmin) {
    formFields = formFields.map((formField) => ({
      ...formField,
      validations: [],
    }));
  }

  return formFields;
}

const SrSunSetupDriverJobPage = ({
  history,
  userAgent,
  job,
  sunlock,
  codeboxLock,
  yardSignUrl,
  listing,
  driverId,
  jobOrderId,
  onUpdateJob,
  onUpdateListing,
  handleSunlockRegistration,
  handleYardSignRegistration,
  handleYardSignTakeDownScan,
  handleCodeBoxRegistration,
  handleComboLockboxRegistration,
  handleSupraRegistration,
  appUrl,
  requestGeolocationPermission,
  getCurrentLocation,
  isAdmin,
}) => {
  const showJobCompletionPhotosFormField = ![
    JOB_TYPE_CONFIRM_LISTING_INFORMATION,
    JOB_TYPE_MEDIA_3D_TOUR,
  ].includes(job.jobTypeId);

  return (
    <Formik
      enableReinitialize
      onSubmit={emptyFunction}
      initialValues={createInitialValues({
        formFields: getFormFields({
          job,
          listing,
          showJobCompletionPhotosFormField,
        }),
        defaultValues: {
          ...job,
          sunlockId: sunlock?.id,
          codeboxLockId: codeboxLock?.id,
          yardSignUrl: yardSignUrl,
          listingPhotoUrls: listing.photoUrls,
          codeboxLocationPhotos: listing.codeboxLocationPhotos,
          codeboxInstructions: listing.codeboxInstructions,
        },
        isAdmin,
      })}
      validate={(formValues) => {
        const formFields = getFormFields({
          job,
          sunlock,
          codeboxLock,
          listing,
          formValues,
          showJobCompletionPhotosFormField,
          isAdmin,
        });
        const errors = createFormValidationFn(formFields)(formValues);
        return errors;
      }}
      render={(formikBag) => {
        const formFields = getFormFields({
          job,
          listing,
          sunlock,
          codeboxLock,
          formValues: formikBag.values,
          showJobCompletionPhotosFormField,
          isAdmin,
          onChecklistChange: (checklist) => {
            formikBag.setFieldValue('checklist', checklist);
            onUpdateJob({
              updates: {
                checklist,
              },
            });
          },
          onCompletionPhotoUrls: (completionPhotoUrls) => {
            onUpdateJob({
              updates: {
                completionPhotoUrls,
              },
            });
          },
          onCodeboxLocationPhotoUrls: (codeboxLocationPhotos) => {
            onUpdateListing({
              updates: {
                codeboxLocationPhotos,
              },
            });
          },
          onCodeboxInstructions: () => {
            onUpdateListing({
              updates: {
                codeboxInstructions: formikBag.values.codeboxInstructions || null,
              },
            });
          },
          onDriverNotesChange: () => {
            onUpdateJob({
              updates: {
                driverNotes: formikBag.values.driverNotes || null,
              },
            });
          },
          onDetailsChange: (formFieldId) => {
            return (value) => {
              onUpdateJob({
                updates: {
                  details: {
                    ...(formikBag.values.details || {}),
                    [formFieldId]: value,
                  },
                },
              });
            };
          },
        });

        const onStatusChange = (status) => {
          formikBag.setFieldValue('status', status);

          const formValues = {
            ...formikBag.values,
            status,
          };

          const formErrors = createFormValidationFn(
            getFormFields({
              job,
              sunlock,
              codeboxLock,
              listing,
              formValues,
              isAdmin,
            }),
          )(formValues);
          const formFieldIdsWithErrors = Object.keys(formErrors);

          if (!formFieldIdsWithErrors.length) {
            onUpdateJob({
              updates: {
                status,
              },
            }).then(() => {
              history.push(`/sunsetup/drivers/${driverId}/orders/${jobOrderId}`);
            });
          } else {
            formFieldIdsWithErrors.forEach((formFieldId) => {
              formikBag.setFieldTouched(formFieldId, true, true);
            });
            scrollToFormField({
              formFieldId: formFieldIdsWithErrors[0],
            });
          }
        };

        const getFormFieldsById = createFormFieldsByIdGetter(formFields);
        const checklistFormField = getFormFieldsById('checklist');
        const listingAssignedItemsMapping = job.listingAssignedItems.reduce((acc, item) => {
          acc[item.inventoryItemTypeId] = item;
          return acc;
        }, {});

        return (
          <Form>
            <div styleName="card">
              <span styleName="jobType">{getJobTypeToActionMapping({ job, listing })}</span>
              <br />
              <span styleName={classnames('jobStatus', job.status.toLowerCase())}>
                {srJobStatusToDriverStatusMapping[job.status]}
              </span>
            </div>

            {checklistFormField && (
              <div styleName="card">
                <SrFormikField
                  formField={getFormFieldsById('checklist')}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
                {job.jobTypeId === JOB_TYPE_CONFIRM_LISTING_INFORMATION && (
                  <SrText font="greycliff" fontSize="14px" weight="500" lineHeight="20px">
                    This order is meant to be completed only if the listing in vacant, or if you are
                    entering an occupied home for other jobs (like interior photos). If the home is
                    occupied and you are not entering the home for another job, please mark this job
                    as incomplete and leave a note.
                  </SrText>
                )}
              </div>
            )}

            {[JOB_TYPE_MEDIA, JOB_TYPE_MEDIA_CURBSHOT].includes(job.jobTypeId) && (
              <SrSunSetupDriverJobMediaPhotos
                listingId={listing.id}
                driverId={driverId}
                jobOrderId={jobOrderId}
                jobId={job.id}
                formikBag={formikBag}
              />
            )}

            {job.jobTypeId === JOB_TYPE_MEDIA_3D_TOUR && (
              <div styleName="card" className="fieldsContainer">
                <SrFormikField
                  formField={getFormFieldsById('details.3dTourUrl')}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                />
              </div>
            )}

            {job.jobTypeId === JOB_TYPE_CONDITION_REPORT && (
              <SrSunSetupDriverJobConditionReportLink userAgent={userAgent} />
            )}

            {(job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL ||
              job.jobTypeId === JOB_TYPE_SUNLOCK_TAKEDOWN) && (
              <SrSunSetupDriverJobSunlockBarcode
                sunlock={sunlock}
                formikBag={formikBag}
                itemScans={job.jobItemScans}
                listingAssignedItems={listingAssignedItemsMapping}
                isSunlockTakedown={job.jobTypeId === JOB_TYPE_SUNLOCK_TAKEDOWN}
                handleSunlockRegistration={handleSunlockRegistration}
                getCurrentLocation={getCurrentLocation}
                requestGeolocationPermission={requestGeolocationPermission}
              />
            )}

            {(job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL ||
              job.jobTypeId === JOB_TYPE_CODEBOX_TAKEDOWN) && (
              <SrSunSetupDriverJobCodeboxBarcode
                partnerId={listing.partnerId}
                codeboxLock={codeboxLock}
                formikBag={formikBag}
                itemScans={job.jobItemScans}
                listingAssignedItems={listingAssignedItemsMapping}
                isCodeboxTakedown={job.jobTypeId === JOB_TYPE_CODEBOX_TAKEDOWN}
                handleCodeBoxRegistration={handleCodeBoxRegistration}
                getCurrentLocation={getCurrentLocation}
                requestGeolocationPermission={requestGeolocationPermission}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxCodes codeboxLock={codeboxLock} />
            )}

            {codeboxLock &&
              (job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL ||
                job.jobTypeId === JOB_TYPE_CHECK_DOOR_LOCK ||
                job.jobTypeId === JOB_TYPE_PROPERTY_CHECK) && (
                <SrSunSetupDriverJobSelfTourTest
                  userAgent={userAgent}
                  formikBag={formikBag}
                  appUrl={appUrl}
                  listing={listing}
                />
              )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxLocationPhotos
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
              />
            )}

            {job.jobTypeId === JOB_TYPE_CODEBOX_INSTALL && (
              <SrSunSetupDriverJobCodeboxSpecialInstructions
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
              />
            )}

            {job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL && (
              <SrSunSetupDriverJobSunlockCodes sunlock={sunlock} />
            )}

            {job.jobTypeId === JOB_TYPE_CONFIRM_LISTING_INFORMATION && (
              <SrSunSetupDriverJobConfirmListingInformation
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
                onUpdateJob={onUpdateJob}
              />
            )}

            {job.jobTypeId === JOB_TYPE_YARD_SIGN_SETUP &&
              listing.partnerId !== PARTNER_ID_HOMERIVER && (
                <SrSunSetupDriverJobYardSignSetup
                  partnerId={listing.partnerId}
                  formikBag={formikBag}
                  itemScans={job.jobItemScans}
                  yardSignUrl={yardSignUrl}
                  getCurrentLocation={getCurrentLocation}
                  requestGeolocationPermission={requestGeolocationPermission}
                  handleYardSignRegistration={handleYardSignRegistration}
                />
              )}
            {job.jobTypeId === JOB_TYPE_YARD_SIGN_TAKEDOWN &&
              listing.partnerId !== PARTNER_ID_HOMERIVER && (
                <SrSunSetupDriverJobYardSignTakedown
                  partnerId={listing.partnerId}
                  formikBag={formikBag}
                  itemScans={job.jobItemScans}
                  listingAssignedItems={listingAssignedItemsMapping}
                  getCurrentLocation={getCurrentLocation}
                  requestGeolocationPermission={requestGeolocationPermission}
                  handleYardSignTakeDownScan={handleYardSignTakeDownScan}
                />
              )}

            {sunlock &&
              (job.jobTypeId === JOB_TYPE_SUNLOCK_INSTALL ||
                job.jobTypeId === JOB_TYPE_REPLACE_SUNLOCK_BATTERY ||
                job.jobTypeId === JOB_TYPE_CHECK_DOOR_LOCK ||
                job.jobTypeId === JOB_TYPE_PROPERTY_CHECK) && (
                <SrSunSetupDriverJobSelfTourTest
                  userAgent={userAgent}
                  formikBag={formikBag}
                  appUrl={appUrl}
                  listing={listing}
                />
              )}

            {job.jobTypeId === JOB_TYPE_COMBO_LOCK_INSTALL && (
              <SrSunSetupDriverJobComboLockboxCodes
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
                onUpdateJob={onUpdateJob}
                onUpdateListing={onUpdateListing}
              />
            )}

            {(job.jobTypeId === JOB_TYPE_COMBO_LOCK_INSTALL ||
              job.jobTypeId === JOB_TYPE_COMBO_LOCK_TAKEDOWN) && (
              <SrSunSetupDriverJobComboLockboxBarcode
                itemScans={job.jobItemScans}
                listingAssignedItems={listingAssignedItemsMapping}
                isComboLockboxTakedown={job.jobTypeId === JOB_TYPE_COMBO_LOCK_TAKEDOWN}
                handleComboLockboxRegistration={handleComboLockboxRegistration}
                getCurrentLocation={getCurrentLocation}
                requestGeolocationPermission={requestGeolocationPermission}
              />
            )}

            {(job.jobTypeId === JOB_TYPE_SUPRA_LOCK_INSTALL ||
              job.jobTypeId === JOB_TYPE_SUPRA_LOCK_TAKEDOWN) && (
              <SrSunSetupDriverJobSupraBarcode
                itemScans={job.jobItemScans}
                listingAssignedItems={listingAssignedItemsMapping}
                isSupraTakedown={job.jobTypeId === JOB_TYPE_SUPRA_LOCK_TAKEDOWN}
                handleSupraRegistration={handleSupraRegistration}
                getCurrentLocation={getCurrentLocation}
                requestGeolocationPermission={requestGeolocationPermission}
              />
            )}

            {showJobCompletionPhotosFormField && (
              <SrSunSetupDriverJobCompletionPhotos
                text={
                  job.jobTypeId !== JOB_TYPE_CODEBOX_INSTALL
                    ? null
                    : 'The completion photo should show the CodeBox location and ' +
                      'verify that, if the key string is installed, it is long ' +
                      'enough to reach the door lock.'
                }
                formikBag={formikBag}
                getFormFieldsById={getFormFieldsById}
              />
            )}

            <div styleName="card">
              <SrFormikField
                formField={getFormFieldsById('driverNotes')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            </div>

            {(isAdmin || ![JOB_STATUS_FAILED, JOB_STATUS_COMPLETED].includes(job.status)) && (
              <SrBox margin="40px 0 0">
                <ButtonDeprecated
                  theme={BUTTON_THEME_RED_ADOBE}
                  onClick={() => onStatusChange(JOB_STATUS_FAILED)}
                >
                  Incomplete
                </ButtonDeprecated>
                <SrBox height="18px" />
                <ButtonDeprecated
                  theme={BUTTON_THEME_GREEN_ADOBE}
                  onClick={() => onStatusChange(JOB_STATUS_COMPLETED)}
                >
                  Job Complete
                </ButtonDeprecated>
              </SrBox>
            )}
          </Form>
        );
      }}
    />
  );
};

SrSunSetupDriverJobPage.propTypes = {
  userAgent: PropTypes.object.isRequired,
  job: PropTypes.object.isRequired,
  sunlock: PropTypes.object,
  yardSignUrl: PropTypes.string,
  listing: PropTypes.object.isRequired,
  jobOrderId: PropTypes.number.isRequired,
  driverId: PropTypes.number.isRequired,
  onUpdateJob: PropTypes.func.isRequired,
  onUpdateListing: PropTypes.func.isRequired,
  handleSunlockRegistration: PropTypes.func.isRequired,
  handleYardSignRegistration: PropTypes.func.isRequired,
  handleCodeBoxRegistration: PropTypes.func.isRequired,
  handleComboLockboxRegistration: PropTypes.func.isRequired,
  handleSupraRegistration: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  appUrl: PropTypes.string.isRequired,
};

SrSunSetupDriverJobPage.displayName = 'SrSunSetupDriverJobPage';

export default SrSunSetupDriverJobPage;
